import classNames from 'classnames/bind';
import React from 'react';
import styles from './Header.scss';
import { AccessibleTextSchemeBackground } from 'mini-color/lib/schema/ColorScheme';
import { getAccessibleTextColor } from 'mini-color/lib/utils/colorSchemeUtils';
import { HeaderContextProvider } from '../context/HeaderContext';
import { HeaderIconsContainer } from './HeaderIconsContainer';
import type { HeaderTitleAlign } from './HeaderTitle';
import { mergeOverrideStyles } from 'mini-styles/lib/utils/mergeOverrideStyles';
import { useTheme } from 'mini-theme';
import type { ColorScheme } from 'mini-color/lib/schema/ColorScheme';

const cx = classNames.bind(styles);

export interface HeaderProps {
    titleContent?: JSX.Element;
    leftContent?: JSX.Element;
    rightContent?: JSX.Element;
    ariaLabel?: string;
    className?: string;
    showBorder?: boolean;
    primary?: boolean;
    colorScheme?: ColorScheme;
    titleAlign?: HeaderTitleAlign;
    isExpandableHeader?: boolean;
}

export const Header = (props: HeaderProps) => {
    const { isDarkTheme } = useTheme();
    const {
        titleContent,
        leftContent,
        rightContent,
        className,
        ariaLabel,
        showBorder = false,
        primary = !isDarkTheme,
        colorScheme,
        titleAlign = 'center',
        isExpandableHeader,
    } = props;

    /**
     * If title is not present or ariaLabel has been explicitly provided for header, then add aria props with role heading
     */
    const headerRole =
        !titleContent || ariaLabel
            ? { role: 'heading', 'aria-level': 1, 'aria-label': ariaLabel }
            : {};

    let colorStyles = null;
    if (colorScheme) {
        if (primary) {
            colorStyles = mergeOverrideStyles({
                background: colorScheme.schemePrimary,
                color: getAccessibleTextColor(
                    colorScheme,
                    AccessibleTextSchemeBackground.schemePrimary
                ),
            });
        } else {
            colorStyles = mergeOverrideStyles({
                color: colorScheme.schemePrimary,
            });
        }
    }

    const headerStyleOverride = isExpandableHeader ? 'composeHeaderContainer' : 'headerContainer';

    return (
        <HeaderContextProvider state={{ primary, colorScheme, titleAlign }}>
            <header
                {...headerRole}
                className={cx(
                    headerStyleOverride,
                    primary && styles.primary,
                    showBorder && styles.showBorder,
                    colorStyles,
                    className
                )}>
                {leftContent ? leftContent : <HeaderIconsContainer />}
                {titleContent}
                {rightContent ? rightContent : <HeaderIconsContainer rightAligned />}
            </header>
        </HeaderContextProvider>
    );
};
